import React, { useEffect, useState } from "react";
import Papa from 'papaparse';
import dayjs from "dayjs";
import { map, find } from 'lodash';
import { useForm } from "antd/es/form/Form";
import { useNavigate, Link } from "react-router-dom";
import { Col, Form, Input, Row, Select, Button, Upload, message, DatePicker, Flex, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../store/AuthContext";
import { addReport, clearData, getAccountMetadata, getCatalogMetadata } from "./reportSlice";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Mapper } from "./Mapper";
import { Preview } from "./Preview";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import classNames from "classnames";
import css from "../Dashboard/Dashboard.module.css"; 

export const Report = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = useForm();
  
  const [step, setStep] = useState(1);
  const [fields, setFields] = useState({});
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);
  const { user } = useAuth(); 
  const { addReportInProgress, getAccountMetadataInProgress, getCatalogMetadataInProgress } =
	useSelector((state) => state?.report) || {};

  const onSubmit = async (values) => {
	let mappedRows = [], 
		numericFields = ['quantity'], 
		booleanFields = ['update_stock']
	for(let row of rows) {
		if(row.validRow !== true)
			continue;
			
		let mappedRow = {
			//
		};
		for(let field of Object.keys(fields)) {
			const value = row[fields[field]];
			if(numericFields.includes(field))
				mappedRow[field] = Number(value);
			else if(booleanFields.includes(field))
				mappedRow[field] = (value == 'Y');
			else
				mappedRow[field] = value.trim();
		}
		mappedRows.push(mappedRow);
	}
	
	const result = await dispatch(
		addReport({payload: mappedRows, file, user: user.email})
	);
	form.resetFields();
	navigate("/report/logs", { state: { from: "/uploaded" } });
  };
  
  const submitFieldMap = (formData) => {
	// setFields(formData.fields);
	const accounts = map(rows, row=>row[formData.fields['account_number']]);
	const products = map(rows, row=>row[formData.fields['catalogue_number']]);
	Promise.all([
		dispatch(getAccountMetadata({accountNumbers: accounts})),
		dispatch(getCatalogMetadata({catalogNumbers: products}))
	]).then(([accountMetadata, catalogMetadata])=>{
		
		let newRows = [];
		for(let row of rows) {
			const catalogRow = find(catalogMetadata.payload, r=>r.Catalogue_Number == row[formData.fields['catalogue_number']]);
			const accountRow = find(accountMetadata.payload, r=>r.Identifier == row[formData.fields['account_number']]);
			let newRow = {...row};
			newRow.upc = catalogRow ? catalogRow.UPC : 'N/A';
			newRow.account_name = accountRow ? accountRow.Account : 'N/A';
			if(newRow.account_name == 'N/A' || newRow.upc == "N/A") 
				newRow.validRow = 'Missing account or catalog entry';
			else if(!dayjs(row[formData.fields['date_shipped']], 'YYYY-MM-DD', true).isValid()) 
				newRow.validRow = 'Invalid shipping date';
			else	
				newRow.validRow = true;
				
			newRows.push(newRow)
		}
		const newHeaders = [
			...headers, 
			'upc',
			'account_name',
		];
		const newFields = {
			...formData.fields, 
			'upc': 'upc',
			'account_name': 'account_name',
		}
		
		setRows(newRows);
		setHeaders(newHeaders);
		setFields(newFields);
		setStep(3);
	});
  }
  
  const beforeUpload = (file) => {
	const isCsv = file.type === 'text/csv' || file.type === 'application/csv';
	if (!isCsv) {
	  message.error('You can only upload CSV file!');
	}
	if(isCsv) {
		Papa.parse(file, {
			header: true,
			skipEmptyLines: true,
			complete: function(results) {
				if(results.data && results.data.length > 0) {
					const { data } = results;
					const row = data[0];
					setHeaders(Object.keys(row));
					setRows(data);
					setFile(file.name);
					setStep(2);
				}
			},
			error: function(error) {
				console.error('Error parsing CSV:', error);
			},
			transform: function(value, header) {
				value = value.trim();
				if(value.startsWith("'") && !value.endsWith("'")) {
					value = value.substr(1);
				}
				return value;
			  },
		});
	}
	return false;
  };
  
  const rules = [
	{
	  required: true,
	  message: "This field is required",
	},
  ];
  
  const submitReady = Object.keys(fields).length > 0;

  return (
	<>
	  <PageHeader title="Report Uploader" extraClass={css.reportTitle} />
		<Form
		form={form}
		name="importReportForm"
		autoComplete="off"
		layout="vertical"
		onFinish={onSubmit}
		>
			<Flex className={css.root} vertical>
				<div className={"ibox-holder"}>
					<div className={css.stepsHolder}>
						<div className={classNames(css.stepItem, {[css.past]: step > 1, [css.current]: step == 1})}>
							<span className={css.stepNumber}>1</span>
							<div className={css.stepDescription}>
								<h3 className={css.stepTitle}>Step 1: Choose .csv</h3>
								<p className={css.stepText}>Upload csv file from your computer. File must be in .csv format with headers in row 1, and with no total rows at the bottom</p>
							</div>
						</div>
						<div className={classNames(css.stepItem,  {[css.past]: step ==3 , [css.current]: step == 2, [css.past]: step == 1})}>
							<span className={css.stepNumber}>2</span>
							<div className={css.stepDescription}>
								<h3 className={css.stepTitle}>Step 2: Match Headers</h3>
								<p className={css.stepText}>Match the required header columns from the file. The file should contain all the required fields marked with *</p>
							</div>
						</div>
						<div className={classNames(css.stepItem, {[css.past]: step < 3, [css.current]: step == 3})}>
							<span className={css.stepNumber}>3</span>
							<div className={css.stepDescription}>
								<h3 className={css.stepTitle}>Step 3: Upload Your Files</h3>
								<p className={css.stepText}>Check the result table. Click Upload to load your files into the database. If you make a mistake click “View Logs” to delete and entry</p>
							</div>
						</div>
					</div>
					<SectionHeader title="File Upload" />
					{step == 1 && <Row gutter={16}>
						<Col xs={24} md={8}>
						<Form.Item
							name="file"
							className={classNames(css.reportFormItem, css.inlineFormItem, css.uploadCsv)}
							
							rules={rules}
						>
							<Upload
								name="csv"
								showUploadList={false}
								beforeUpload={beforeUpload}
								>
								<Button>Choose CSV File</Button>
							</Upload>
						</Form.Item>
						
						</Col>
					</Row>}
				</div>
				{(headers.length>0 && step == 2) && <>
					<Mapper headers={headers} onSubmit={submitFieldMap} loading={(getAccountMetadataInProgress && getCatalogMetadataInProgress)} />
				</>}
				{step == 3 && <div className={"ibox-holder"}>
					<Preview headers={headers} rows={rows} />
					<Form.Item
						className={classNames(css.tableButton)}
					>
						<Button
							size="large"
							rootClassName={css.submitButton}
							htmlType="submit"
							loading={false}
							disabled={!submitReady}
							type="primary"
						>
							Upload
						</Button> 
						{addReportInProgress && <Spin size="small" />}
					</Form.Item>
				</div>}
			</Flex>
		</Form>
	</>
  );
};
